import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, LockOpen, Loader2, UserCog, X as XIcon, ShieldCheck, FileText } from "lucide-react";
import { toast } from "sonner";

const ORANGE = "#FB923C";

function ClassPicker({ expeditions = [], value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="pacing-class-picker" className="w-56 bg-white/5 border-white/10"><SelectValue placeholder="Choose a class…" /></SelectTrigger>
      <SelectContent>{expeditions.map((e) => <SelectItem key={e.expedition_id} value={e.expedition_id} data-testid={`pacing-class-opt-${e.join_code || e.expedition_id}`}>{e.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function BlockPills({ n, value, onChange, testidPrefix, allowFollow }) {
  const opts = [];
  if (allowFollow) opts.push({ v: "class", label: "Class setting" });
  opts.push({ v: "0", label: "None" });
  for (let i = 1; i <= n; i++) opts.push({ v: String(i), label: `Block ${i}` });
  opts.push({ v: "all", label: "All open" });
  return (
    <div className="flex flex-wrap gap-1.5">
      {opts.map((o) => (
        <button key={o.v} data-testid={`${testidPrefix}-${o.v}`} onClick={() => onChange(o.v)}
          className={`h-8 px-3 rounded-full text-xs border transition-colors ${value === o.v ? "border-[#22D3EE]/70 bg-[#22D3EE]/15 text-[#22D3EE]" : "border-white/15 text-slate-300 hover:bg-white/5"}`}>{o.label}</button>
      ))}
    </div>
  );
}

function UnitPacingCard({ t, onSave }) {
  const n = t.blocks.length;
  const otValue = t.open_through == null ? "all" : String(t.open_through);
  const locked = new Set(t.locked_tests || []);
  const save = (open_through, locked_tests) => onSave(t.track, { open_through, locked_tests });
  const setBlocks = (v) => save(v === "all" ? null : Number(v), [...locked]);
  const toggleTest = (id) => { const s = new Set(locked); s.has(id) ? s.delete(id) : s.add(id); save(t.open_through, [...s]); };
  const allIds = t.tests.map((x) => x.id);
  return (
    <div className="hq-glass rounded-2xl p-5 border border-white/10" data-testid={`pacing-unit-${t.track}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <h3 className="font-display text-xl">{t.name}</h3>
        <span className="text-xs text-muted-foreground">{t.open_through == null ? `All ${n} blocks open` : t.open_through === 0 ? "No blocks open" : `Open through Block ${t.open_through}`} · {locked.size}/{allIds.length} tests locked</span>
      </div>
      <p className="text-xs text-slate-300 mb-1.5">Lessons, drills &amp; block tasks open through…</p>
      <BlockPills n={n} value={otValue} onChange={setBlocks} testidPrefix={`pacing-${t.track}-blocks`} />
      <div className="flex items-center justify-between gap-2 mt-4 mb-1.5 flex-wrap">
        <p className="text-xs text-slate-300">Tests</p>
        <div className="flex gap-2">
          <button data-testid={`pacing-${t.track}-lock-all`} onClick={() => save(t.open_through, allIds)} className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Lock all</button>
          <button data-testid={`pacing-${t.track}-open-all`} onClick={() => save(t.open_through, [])} className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1"><LockOpen className="w-3 h-3" /> Open all</button>
        </div>
      </div>
      <div className="grid gap-1.5">
        {t.tests.map((x) => {
          const isLocked = locked.has(x.id);
          const blockLocked = x.block != null && t.open_through != null && x.block > t.open_through;
          return (
            <label key={x.id} data-testid={`pacing-test-${x.id}`} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 border ${isLocked || blockLocked ? "border-white/5 bg-white/[0.02]" : "border-[#34D399]/30 bg-[#34D399]/5"}`}>
              <span className="text-sm flex items-center gap-2 min-w-0">
                {isLocked || blockLocked ? <Lock className="w-3.5 h-3.5 shrink-0" style={{ color: ORANGE }} /> : <LockOpen className="w-3.5 h-3.5 text-[#34D399] shrink-0" />}
                <span>{x.title}</span>
                {blockLocked && !isLocked && <span className="text-[10px] text-slate-500 shrink-0">(block closed)</span>}
              </span>
              <span className="flex items-center gap-2 text-xs text-slate-400 shrink-0">{isLocked ? "Locked" : "Open"}<Switch data-testid={`pacing-test-switch-${x.id}`} checked={!isLocked} onCheckedChange={() => toggleTest(x.id)} /></span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function StudentOverrides({ data, expId, reload }) {
  const [sid, setSid] = useState("");
  const [saving, setSaving] = useState(false);
  const student = data.students.find((s) => s.user_id === sid);
  const withOverrides = data.students.filter((s) => Object.keys(s.override || {}).length > 0);

  const save = async (track, patch) => {
    const cur = student.override?.[track] || {};
    const body = { track, open_through: cur.open_through ?? null, tests: { ...(cur.tests || {}) }, ...patch };
    setSaving(true);
    try {
      await api.put(`/pacing/${expId}/student/${sid}`, body);
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the override."); }
    finally { setSaving(false); }
  };
  const clearAll = async (s) => {
    setSaving(true);
    try {
      await Promise.all(data.tracks.map((t) => api.put(`/pacing/${expId}/student/${s.user_id}`, { track: t.track, open_through: null, tests: {} })));
      await reload();
      toast.success(`${s.name || s.email}: back to the class pacing.`);
    } catch (e) { toast.error("Could not clear overrides."); }
    finally { setSaving(false); }
  };

  return (
    <div className="hq-glass rounded-2xl p-5 border border-t-[#a5b4fc]/40 border-white/10" data-testid="pacing-student-overrides">
      <h3 className="font-display text-xl flex items-center gap-2"><UserCog className="w-5 h-5 text-[#a5b4fc]" /> Per-Explorer overrides</h3>
      <p className="text-xs text-muted-foreground mt-1 mb-3">Let one student work ahead (or hold one back) without changing the class. Overrides win over the class setting.</p>
      {data.students.length === 0 ? <p className="text-sm text-muted-foreground">No Explorers in this class yet.</p> : (
        <Select value={sid} onValueChange={setSid}>
          <SelectTrigger data-testid="pacing-student-picker" className="w-full sm:w-80 bg-white/5 border-white/10"><SelectValue placeholder="Choose an Explorer…" /></SelectTrigger>
          <SelectContent>{data.students.map((s) => <SelectItem key={s.user_id} value={s.user_id} data-testid={`pacing-student-opt-${s.user_id}`}>{s.name || s.email}{Object.keys(s.override || {}).length ? " · has overrides" : ""}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {student && (
        <div className="mt-4 space-y-4" data-testid="pacing-student-editor">
          {data.tracks.map((t) => {
            const ov = student.override?.[t.track] || {};
            const otVal = ov.open_through == null ? "class" : String(ov.open_through);
            return (
              <div key={t.track} className="rounded-xl border border-white/10 p-3" data-testid={`pacing-student-unit-${t.track}`}>
                <p className="text-sm font-medium mb-1.5">{t.name} <span className="text-xs text-muted-foreground">· class: {t.open_through == null ? "all blocks" : `through Block ${t.open_through}`}</span></p>
                <BlockPills n={t.blocks.length} value={otVal} allowFollow testidPrefix={`pacing-student-${t.track}-blocks`}
                  onChange={(v) => save(t.track, { open_through: v === "class" ? null : v === "all" ? "all" : Number(v) })} />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {t.tests.map((x) => {
                    const st = ov.tests?.[x.id] || "class";
                    const classLocked = (t.locked_tests || []).includes(x.id);
                    const next = st === "class" ? "open" : st === "open" ? "locked" : "class";
                    const cls = st === "open" ? "border-[#34D399]/50 text-[#34D399] bg-[#34D399]/10" : st === "locked" ? "border-[#FB923C]/50 text-[#FB923C] bg-[#FB923C]/10" : "border-white/15 text-slate-400";
                    return (
                      <button key={x.id} data-testid={`pacing-student-test-${x.id}`} onClick={() => save(t.track, { tests: { ...(ov.tests || {}), [x.id]: next === "class" ? null : next } })}
                        title="Click to cycle: class setting → open → locked" className={`h-7 px-2.5 rounded-full text-[11px] border inline-flex items-center gap-1 transition-colors ${cls}`}>
                        {st === "locked" || (st === "class" && classLocked) ? <Lock className="w-3 h-3" /> : <LockOpen className="w-3 h-3" />}
                        {x.title} · {st === "class" ? `class (${classLocked ? "locked" : "open"})` : st}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {saving && <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</p>}
        </div>
      )}
      {withOverrides.length > 0 && (
        <div className="mt-4 border-t border-white/10 pt-3">
          <p className="text-xs text-slate-300 mb-2">Explorers with overrides</p>
          <div className="flex flex-wrap gap-2">
            {withOverrides.map((s) => (
              <span key={s.user_id} data-testid={`pacing-override-chip-${s.user_id}`} className="inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1 rounded-full text-xs border border-[#a5b4fc]/40 bg-[#a5b4fc]/10 text-[#c7d2fe]">
                <button onClick={() => setSid(s.user_id)} className="hover:underline">{s.name || s.email}</button>
                <button data-testid={`pacing-override-clear-${s.user_id}`} onClick={() => clearAll(s)} title="Clear overrides — follow class pacing" className="p-0.5 rounded-full hover:bg-white/10"><XIcon className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SecureModeCard({ cfg, onSave }) {
  const warnings = Math.max(0, (cfg?.max_exits || 2) - 1);
  return (
    <div className="hq-glass rounded-2xl p-5 border border-white/10 border-t-[#22D3EE]/40" data-testid="secure-mode-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-[#22D3EE] mt-0.5 shrink-0" />
          <div>
            <h3 className="font-display text-xl">Secure Test Mode <span className="text-xs font-sans text-muted-foreground">· Unit Tests, Final Exam &amp; Unit Task</span></h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">Finals run fullscreen with the site menu hidden and copy/paste/right-click blocked. Switching tabs, opening another window, or leaving fullscreen is recorded; after the allowed warnings the test is submitted automatically. Exits show up as a shield next to the score in Test Scores. Turn it off for a class when students test from home.</p>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-200 shrink-0"><span data-testid="secure-mode-state">{cfg?.enabled ? "On" : "Off"}</span><Switch data-testid="secure-mode-toggle" checked={!!cfg?.enabled} onCheckedChange={(v) => onSave({ enabled: v, max_exits: cfg?.max_exits || 2 })} /></label>
      </div>
      <div className={`mt-4 flex items-center gap-3 flex-wrap ${cfg?.enabled ? "" : "opacity-50"}`}>
        <span className="text-xs text-slate-300">Warnings before auto-submit:</span>
        {[0, 1, 2, 3].map((w) => (
          <button key={w} data-testid={`secure-warnings-${w}`} disabled={!cfg?.enabled} onClick={() => onSave({ enabled: true, max_exits: w + 1 })}
            className={`h-8 px-3 rounded-full text-xs border transition-colors ${warnings === w ? "border-[#22D3EE]/70 bg-[#22D3EE]/15 text-[#22D3EE]" : "border-white/15 text-slate-300 hover:bg-white/5"}`}>{w === 0 ? "None — submit on first exit" : `${w} warning${w > 1 ? "s" : ""}`}</button>
        ))}
        <a href="/it-admin-note.html" target="_blank" rel="noreferrer" data-testid="it-admin-note-link" className="ml-auto inline-flex items-center gap-1.5 text-xs text-[#22D3EE] hover:underline"><FileText className="w-3.5 h-3.5" /> Note for your IT admin (true Chromebook lockdown)</a>
      </div>
    </div>
  );
}

export default function PacingTab({ expeditions = [] }) {
  const [expId, setExpId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (expeditions.length && !expId) setExpId(expeditions[0].expedition_id); }, [expeditions, expId]);
  const load = useCallback(async () => {
    if (!expId) return;
    const r = await api.get(`/pacing/${expId}`);
    setData(r.data);
  }, [expId]);
  useEffect(() => { if (!expId) return; setLoading(true); load().catch(() => toast.error("Could not load pacing.")).finally(() => setLoading(false)); }, [expId, load]);

  const saveUnit = async (track, body) => {
    try {
      const r = await api.put(`/pacing/${expId}`, { track, ...body });
      setData((d) => ({ ...d, tracks: d.tracks.map((t) => (t.track === track ? { ...t, open_through: r.data.open_through, locked_tests: r.data.locked_tests } : t)) }));
      toast.success("Pacing saved — students see it on their next page load.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save pacing."); }
  };

  const saveSecure = async (body) => {
    try {
      const r = await api.put(`/pacing/${expId}/secure`, body);
      setData((d) => ({ ...d, secure_test: r.data }));
      toast.success(r.data.enabled ? "Secure Test Mode on for this class." : "Secure Test Mode off for this class.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
  };

  if (expeditions.length === 0) return <p className="text-sm text-muted-foreground py-10 text-center">Create a class first to set its pacing.</p>;

  return (
    <div className="space-y-5" data-testid="pacing-tab">
      <div className="flex items-start gap-4 flex-wrap">
        <ClassPicker expeditions={expeditions} value={expId} onChange={setExpId} />
        <p className="text-sm text-muted-foreground max-w-2xl">Keep the class together: choose how far each unit is open, and flip individual tests on when you're ready. Locked items stay visible to students but greyed out with <span className="text-[#FB923C]">“Locked by your Guide”</span>. New classes start with every test locked; classes made before this feature start fully open.</p>
      </div>
      {loading || !data ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <>
          <SecureModeCard cfg={data.secure_test} onSave={saveSecure} />
          <div className="grid gap-4 lg:grid-cols-2">
            {data.tracks.map((t) => <UnitPacingCard key={t.track} t={t} onSave={saveUnit} />)}
          </div>
          <StudentOverrides data={data} expId={expId} reload={load} />
        </>
      )}
    </div>
  );
}
